import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import { usePDFIndividual } from "./EstadoFinanciero";
import { useCurrency } from "../../../../shared/contexts/CurrencyContext";

// ── Helpers ───────────────────────────────────────────────────────────────────
const NIVEL_INDENT = { 1: 0, 2: 16, 3: 28, 4: 40 };
const NIVEL_COLOR  = { 1: "#f97316", 2: "var(--text-primary)", 3: "var(--text-secondary)", 4: "var(--text-muted)" };
const NIVEL_WEIGHT = { 1: 700, 2: 700, 3: 600, 4: 400 };
const NIVEL_BG     = { 1: "rgba(249,115,22,0.06)", 2: "var(--hover-bg)", 3: "transparent", 4: "transparent" };

/**
 * Returns the full prior fiscal year range (Jan 1 – Dec 31 of year-1)
 * based on the year of fechaDesde.
 * e.g. fechaDesde = "2026-06-01" → { desde: "2025-01-01", hasta: "2025-12-31" }
 */
function prevFiscalYear(fechaDesde) {
  if (!fechaDesde) return { desde: null, hasta: null };
  const year = new Date(fechaDesde + "T00:00:00").getFullYear() - 1;
  return { desde: `${year}-01-01`, hasta: `${year}-12-31` };
}

// ── Fila ──────────────────────────────────────────────────────────────────────
function Fila({ fila, filaAnt, collapsed, onToggle, comparativo }) {
  const { isUSD, fmtCRC } = useCurrency();
  const indent = NIVEL_INDENT[fila.nivel] ?? 0;

  const pick = (f, fieldCRC, fieldUSD) =>
    Number(isUSD ? (f?.[fieldUSD] ?? 0) : (f?.[fieldCRC] ?? 0));

  const ba    = pick(fila, "balance_anterior",     "balance_anterior_usd");
  const deb   = pick(fila, "debitos",              "debitos_usd");
  const cred  = pick(fila, "creditos",             "creditos_usd");
  const final = pick(fila, "saldo_final",          "saldo_final_usd");

  // Comparative: prior year saldo_final, same cuenta
  const finalAnt = filaAnt ? pick(filaAnt, "saldo_final", "saldo_final_usd") : null;
  const variacion = (comparativo && filaAnt != null) ? final - finalAnt : null;

  function fmtCol(v) {
    const sign = v < 0 ? "-" : "";
    return `${sign}${fmtCRC(Math.abs(v))}`;
  }

  const zeroFinal = fmtCRC(0);

  return (
    <tr style={{ background: NIVEL_BG[fila.nivel], borderBottom: "1px solid var(--border-card)" }}>
      <td style={{ fontFamily: "monospace", fontSize: "0.75rem", color: "var(--text-muted)",
        whiteSpace: "nowrap", padding: "6px 10px" }}>
        {fila.codigo}
      </td>
      <td style={{ padding: "6px 8px", paddingLeft: indent + 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {fila.tiene_hijos ? (
            <button onClick={() => onToggle(fila.id)}
              style={{ background: "none", border: "none", cursor: "pointer",
                color: "var(--text-muted)", padding: 0, fontSize: "0.65rem", width: 14, flexShrink: 0 }}>
              <i className={`fa-solid fa-chevron-${collapsed.has(fila.id) ? "right" : "down"}`}/>
            </button>
          ) : <span style={{ width: 14, flexShrink: 0 }}/>}
          <span style={{ fontWeight: NIVEL_WEIGHT[fila.nivel], color: NIVEL_COLOR[fila.nivel],
            fontSize: fila.nivel === 1 ? "0.88rem" : "0.83rem" }}>
            {fila.nombre}
          </span>
        </div>
      </td>
      {[
        { val: ba,    zero: "—" },
        { val: deb,   zero: "—", color: "#60a5fa" },
        { val: cred,  zero: "—", color: "#c084fc" },
        { val: final, zero: zeroFinal, color: final < -0.01 ? "#f87171" : final > 0.01 ? "#4ade80" : "var(--text-muted)" },
      ].map((col, i) => (
        <td key={i} className="text-end" style={{ fontFamily: "monospace",
          fontSize: "0.8rem", padding: "6px 12px",
          color: Math.abs(col.val) < 0.01 ? "var(--text-muted)" : (col.color || "var(--text-secondary)"),
          fontWeight: (i === 3 && fila.nivel <= 2) ? 700 : 400 }}>
          {Math.abs(col.val) < 0.01 ? col.zero : fmtCol(col.val)}
        </td>
      ))}

      {/* ── Comparative columns ── */}
      {comparativo && (
        <>
          {/* Prior year saldo final */}
          <td className="text-end" style={{
            fontFamily: "monospace", fontSize: "0.8rem", padding: "6px 12px",
            color: filaAnt == null ? "var(--text-muted)"
              : finalAnt < -0.01 ? "#f87171"
              : finalAnt > 0.01  ? "#4ade80"
              : "var(--text-muted)",
            fontWeight: fila.nivel <= 2 ? 700 : 400,
            borderLeft: "1px solid rgba(255,255,255,0.06)",
          }}>
            {filaAnt == null
              ? <span style={{ color: "var(--text-muted)" }}>—</span>
              : Math.abs(finalAnt) < 0.01
                ? <span style={{ color: "var(--text-muted)" }}>{fmtCRC(0)}</span>
                : fmtCol(finalAnt)}
          </td>
          {/* Variación */}
          <td className="text-end" style={{
            fontFamily: "monospace", fontSize: "0.8rem", padding: "6px 12px",
            color: variacion == null ? "var(--text-muted)"
              : variacion > 0.01  ? "#4ade80"
              : variacion < -0.01 ? "#f87171"
              : "var(--text-muted)",
            fontWeight: fila.nivel <= 2 ? 700 : 400,
          }}>
            {variacion == null
              ? "—"
              : Math.abs(variacion) < 0.01
                ? <span style={{ color: "var(--text-muted)" }}>—</span>
                : <>
                    <i className={`fa-solid fa-arrow-${variacion > 0 ? "up" : "down"}`}
                      style={{ fontSize: "0.65rem", marginRight: 3 }}/>
                    {fmtCol(variacion)}
                  </>}
          </td>
        </>
      )}
    </tr>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────
export default function BalanceComprobacion() {
  const { t } = useTranslation();
  const { fmtCRC, isUSD, setMultimoneda } = useCurrency();
  const now       = new Date();
  const mesInicio = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const mesFin    = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().split("T")[0];

  const [fechaDesde,  setFechaDesde]  = useState(mesInicio);
  const [fechaHasta,  setFechaHasta]  = useState(mesFin);
  const [data,        setData]        = useState(null);
  const [dataAnt,     setDataAnt]     = useState(null);   // prior year data
  const [loading,     setLoading]     = useState(false);
  const [loadingAnt,  setLoadingAnt]  = useState(false);
  const [soloConMov,  setSoloConMov]  = useState(true);
  const [comparativo, setComparativo] = useState(false);
  const [collapsed,   setCollapsed]   = useState(new Set());
  const pdfBC = usePDFIndividual(
    '/api/finance/reportes/balance-comprobacion-pdf/',
    'balance_comprobacion.pdf',
    { showComparativo: true }
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fechaDesde) qs.set("fecha_desde", fechaDesde);
      if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
      const res = await apiFetch(`/api/finance/reportes/balance-comprobacion/?${qs}`);
      const d   = await res.json();
      setData(d);
      setMultimoneda(d.multimoneda_activa ?? false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta, setMultimoneda]);

  const fetchDataAnt = useCallback(async () => {
    if (!comparativo) { setDataAnt(null); return; }
    setLoadingAnt(true);
    try {
      const { desde, hasta } = prevFiscalYear(fechaDesde);
      const qs = new URLSearchParams();
      if (desde) qs.set("fecha_desde", desde);
      if (hasta) qs.set("fecha_hasta", hasta);
      const res = await apiFetch(`/api/finance/reportes/balance-comprobacion/?${qs}`);
      const d   = await res.json();
      setDataAnt(d);
    } catch (e) { console.error(e); setDataAnt(null); }
    finally { setLoadingAnt(false); }
  }, [comparativo, fechaDesde]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchDataAnt(); }, [fetchDataAnt]);

  const filas      = data?.filas ?? [];
  const filasAnt   = dataAnt?.filas ?? [];
  const childrenOf = buildChildrenOf(filas);
  const hidden     = buildHidden(filas, childrenOf, collapsed);

  // Index prior-year rows by cuenta id for O(1) lookup
  const antById = Object.fromEntries(filasAnt.map(f => [f.id, f]));

  const tieneMovArbol = (id) => {
    const f = filas.find(x => x.id === id);
    if (!f) return false;
    if (f.tiene_movimientos) return true;
    return (childrenOf[id] ?? []).some(tieneMovArbol);
  };

  const visibles = filas.filter(f =>
    !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id))
  );

  const totalBA    = isUSD
    ? filas.filter(f => f.nivel === 1).reduce((a,f) => a + Number(f.balance_anterior_usd ?? 0), 0)
    : filas.filter(f => f.nivel === 1).reduce((a,f) => a + Number(f.balance_anterior    ?? 0), 0);
  const totalDeb   = Number(isUSD ? (data?.total_debitos_usd  ?? 0) : (data?.total_debitos  ?? 0));
  const totalCred  = Number(isUSD ? (data?.total_creditos_usd ?? 0) : (data?.total_creditos ?? 0));
  const diferencia = isUSD ? (totalDeb - totalCred) : Number(data?.diferencia ?? 0);
  const bal        = data?.balanceado ?? true;

  // Totals for comparative footer
  const totalFinal    = isUSD
    ? filas.filter(f => f.nivel === 1).reduce((a,f) => a + Number(f.saldo_final_usd ?? 0), 0)
    : filas.filter(f => f.nivel === 1).reduce((a,f) => a + Number(f.saldo_final     ?? 0), 0);
  const totalFinalAnt = isUSD
    ? filasAnt.filter(f => f.nivel === 1).reduce((a,f) => a + Number(f.saldo_final_usd ?? 0), 0)
    : filasAnt.filter(f => f.nivel === 1).reduce((a,f) => a + Number(f.saldo_final     ?? 0), 0);
  const totalVariacion = comparativo ? totalFinal - totalFinalAnt : null;

  // Number of table columns (for colSpan)
  const numCols = comparativo ? 8 : 6;

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("reportes.balanceComprobacion")}</span>
      </span>
      <div>
        <h2 className="title mt-2 mb-2">{t("reportes.balanceComprobacion")}</h2>
        <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("reportes.movimientosPeriodo")}</p>
      </div>

      <FiltrosBarra
        fechaDesde={fechaDesde} setFechaDesde={setFechaDesde}
        fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
        soloConMov={soloConMov} setSoloConMov={setSoloConMov}
        onActualizar={() => { fetchData(); fetchDataAnt(); }} loading={loading}
        comparativo={comparativo} setComparativo={setComparativo}
        extraButtons={
          <>
            <BotonPDF
              onClick={() => pdfBC.generar(`fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`)}
              loading={pdfBC.loading}
              disabled={pdfBC.loading || loading || !data}
            />
            {pdfBC.modal}
          </>
        }
      />

      {data && (
        <CardsResumen cards={[
          { label: t("reportes.balanceAnterior"), value: totalBA,              color: "var(--text-secondary)", icon: "fa-clock-rotate-left" },
          { label: t("reportes.totalDebitos"),    value: totalDeb,             color: "#60a5fa", icon: "fa-arrow-up" },
          { label: t("reportes.totalCreditos"),   value: totalCred,            color: "#c084fc", icon: "fa-arrow-down" },
          { label: bal ? t("reportes.balanceadoOk") : t("reportes.diferenciaWarn"),
            value: Math.abs(diferencia),
            color: bal ? "#4ade80" : "#f87171",
            icon:  bal ? "fa-scale-balanced" : "fa-triangle-exclamation" },
        ]}/>
      )}

      {/* Comparative period label */}
      {comparativo && (
        <div style={{
          display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap",
        }}>
          <span style={{
            fontSize: "0.76rem", padding: "3px 10px", borderRadius: 5,
            background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.25)",
            color: "#4ade80", fontWeight: 600,
          }}>
            <i className="fa-solid fa-circle" style={{ fontSize: "0.5rem", marginRight: 5, verticalAlign: "middle" }}/>
            {t("reportes.anioActual")}: {fechaDesde} → {fechaHasta}
          </span>
          <span style={{
            fontSize: "0.76rem", padding: "3px 10px", borderRadius: 5,
            background: "rgba(148,163,184,0.1)", border: "1px solid rgba(148,163,184,0.2)",
            color: "var(--text-secondary)", fontWeight: 600,
          }}>
            <i className="fa-solid fa-circle" style={{ fontSize: "0.5rem", marginRight: 5, verticalAlign: "middle" }}/>
            {t("reportes.anioAnterior")}: {prevFiscalYear(fechaDesde).desde} → {prevFiscalYear(fechaDesde).hasta}
            {loadingAnt && <i className="fa-solid fa-circle-notch fa-spin" style={{ marginLeft: 8, fontSize: "0.7rem" }}/>}
          </span>
        </div>
      )}

      <div className={invoiceStyles["table-container"]}>
        <TableHeader
          count={visibles.length} loading={loading}
          onColapsar={() => setCollapsed(new Set(filas.filter(f => f.nivel < 4).map(f => f.id)))}
          onExpandir={() => setCollapsed(new Set())}
        />
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 130 }}>{t("reportes.codigo")}</th>
                <th>{t("reportes.nombre")}</th>
                <th className="text-end" style={{ width: 140 }}>{t("reportes.balAnterior")}</th>
                <th className="text-end" style={{ width: 130 }}>{t("reportes.debitos")}</th>
                <th className="text-end" style={{ width: 130 }}>{t("reportes.creditos")}</th>
                <th className="text-end" style={{ width: 130 }}>{t("reportes.saldoFinal")}</th>
                {comparativo && <>
                  <th className="text-end" style={{ width: 130, borderLeft: "1px solid rgba(255,255,255,0.08)",
                    color: "var(--text-secondary)", fontSize: "0.78rem" }}>
                    {t("reportes.saldoAnioAnt")}
                  </th>
                  <th className="text-end" style={{ width: 120,
                    color: "var(--text-secondary)", fontSize: "0.78rem" }}>
                    {t("reportes.variacion")}
                  </th>
                </>}
              </tr>
            </thead>
            <tbody>
              {loading
                ? <FilaCargando colSpan={numCols} msg={t("reportes.calculandoBalance")}/>
                : visibles.length === 0
                  ? <FilaVacia colSpan={numCols} soloConMov={soloConMov} onMostrarTodas={() => setSoloConMov(false)}/>
                  : visibles.map(f => (
                      <Fila
                        key={f.id}
                        fila={f}
                        filaAnt={comparativo ? (antById[f.id] ?? null) : null}
                        collapsed={collapsed}
                        onToggle={id => setCollapsed(prev => toggle(prev, id))}
                        comparativo={comparativo}
                      />
                    ))}
            </tbody>
            {!loading && data && (
              <tfoot>
                <tr style={{ background: "rgba(249,115,22,0.08)", borderTop: "2px solid rgba(249,115,22,0.4)" }}>
                  <td colSpan={2} style={{ fontWeight: 700, color: "#f97316", padding: "10px 12px", fontSize: "0.87rem" }}>
                    {t("reportes.totales")}
                  </td>
                  <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--text-secondary)", padding: "10px 12px" }}>
                    {fmtCRC(totalBA)}
                  </td>
                  <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 700, color: "#60a5fa", padding: "10px 12px" }}>
                    {fmtCRC(totalDeb)}
                  </td>
                  <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 700, color: "#c084fc", padding: "10px 12px" }}>
                    {fmtCRC(totalCred)}
                  </td>
                  <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 700,
                    color: bal ? "#4ade80" : "#f87171", padding: "10px 12px" }}>
                    {bal
                      ? <><i className="fa-solid fa-check" style={{ marginRight: 4 }}/>{t("reportes.balanceadoCheck")}</>
                      : fmtCRC(Math.abs(diferencia))}
                  </td>
                  {comparativo && <>
                    <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 700,
                      color: "var(--text-secondary)", padding: "10px 12px",
                      borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
                      {loadingAnt
                        ? <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: "0.8rem" }}/>
                        : fmtCRC(Math.abs(totalFinalAnt))}
                    </td>
                    <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 700,
                      color: totalVariacion == null ? "var(--text-muted)"
                        : totalVariacion > 0.01  ? "#4ade80"
                        : totalVariacion < -0.01 ? "#f87171"
                        : "var(--text-muted)",
                      padding: "10px 12px" }}>
                      {totalVariacion == null || loadingAnt
                        ? "—"
                        : <>
                            <i className={`fa-solid fa-arrow-${totalVariacion >= 0 ? "up" : "down"}`}
                              style={{ fontSize: "0.65rem", marginRight: 3 }}/>
                            {fmtCRC(Math.abs(totalVariacion))}
                          </>}
                    </td>
                  </>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  );
}

// ── Shared helpers (exported for reuse) ──────────────────────────────────────
export function buildChildrenOf(filas) {
  const m = {};
  for (const f of filas) {
    if (f.padre_id != null) { if (!m[f.padre_id]) m[f.padre_id] = []; m[f.padre_id].push(f.id); }
  }
  return m;
}
export function buildHidden(filas, childrenOf, collapsed) {
  const h = new Set();
  for (const f of filas) {
    if (collapsed.has(f.id)) {
      const q = [...(childrenOf[f.id] ?? [])];
      while (q.length) { const id = q.shift(); h.add(id); q.push(...(childrenOf[id] ?? [])); }
    }
  }
  return h;
}
export function toggle(prev, id) {
  const next = new Set(prev);
  next.has(id) ? next.delete(id) : next.add(id);
  return next;
}

// ── Shared UI components ─────────────────────────────────────────────────────
// ── Date picker boxed (icono + input) ────────────────────────────────────────
function DateField({ label, value, onChange, icon, iconColor }) {
  return (
    <div style={{ minWidth: 158 }}>
      <label style={{ display: "block", fontSize: "0.66rem", fontWeight: 700,
        letterSpacing: "0.8px", textTransform: "uppercase",
        color: "var(--text-muted)", marginBottom: 6 }}>
        {label}
      </label>
      <div style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)",
        borderRadius: 8, padding: "8px 12px",
        display: "flex", alignItems: "center", gap: 9 }}>
        <i className={`fa-regular ${icon}`}
           style={{ color: iconColor, fontSize: "0.85rem", flexShrink: 0 }}/>
        <input type="date" value={value} onChange={onChange}
          style={{ background: "transparent", border: "none", outline: "none",
            color: "var(--text-primary)", fontSize: "0.85rem", fontWeight: 600,
            colorScheme: "light dark", cursor: "pointer", width: "100%" }}/>
      </div>
    </div>
  );
}

// ── Checkbox estilo "pill" que se ilumina al estar activo ─────────────────────
function CheckPill({ checked, onChange, color, label, icon }) {
  return (
    <label style={{
      display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer",
      padding: "8px 13px", borderRadius: 8, userSelect: "none",
      background: checked ? `${color}14` : "var(--bg-input)",
      border: `1px solid ${checked ? `${color}55` : "var(--border-input)"}`,
      color: checked ? color : "var(--text-secondary)",
      fontSize: "0.82rem", fontWeight: checked ? 600 : 400,
      transition: "all 0.15s",
    }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ accentColor: color, width: 15, height: 15, cursor: "pointer", flexShrink: 0 }}/>
      {icon && <i className={`fa-solid ${icon}`} style={{ fontSize: "0.78rem" }}/>}
      {label}
    </label>
  );
}

// ── Toggle CRC / USD en línea — solo si multimoneda está activa ───────────────
function MonedaToggleInline() {
  const { t } = useTranslation();
  const { isUSD, setIsUSD, multimoneda } = useCurrency();
  if (!multimoneda) return null;

  const OPCIONES = [
    { usd: false, icon: "₡", label: "CRC" },
    { usd: true,  icon: "$", label: "USD" },
  ];

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: "0.66rem", color: "var(--text-muted)", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.7px" }}>
        {t("reportes.verEn")}
      </span>
      <div style={{ display: "inline-flex", padding: 3, borderRadius: 8,
        background: "var(--bg-input)", border: "1px solid var(--border-input)", gap: 2 }}>
        {OPCIONES.map(({ usd, icon, label }) => {
          const active = isUSD === usd;
          return (
            <button key={label} onClick={() => setIsUSD(usd)}
              style={{ display: "inline-flex", alignItems: "center", gap: 5,
                padding: "5px 12px", borderRadius: 6, border: "none",
                background: active ? "#f97316" : "transparent",
                color: active ? "#fff" : "var(--text-muted)",
                fontWeight: active ? 700 : 500, fontSize: "0.8rem",
                cursor: "pointer", transition: "all 0.15s",
                boxShadow: active ? "0 2px 8px rgba(249,115,22,0.35)" : "none" }}>
              <span style={{ fontWeight: 800 }}>{icon}</span>{label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Botón PDF compartido por los tres reportes ───────────────────────────────
export function BotonPDF({ onClick, loading, disabled }) {
  const { t } = useTranslation();
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: "9px 16px", background: "rgba(59,130,246,0.13)",
        border: "1px solid rgba(59,130,246,0.45)", borderRadius: 8,
        color: "#60a5fa", fontWeight: 600, fontSize: "0.84rem",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex", alignItems: "center", gap: 7,
        opacity: disabled ? 0.5 : 1, transition: "all 0.15s" }}
      onMouseOver={e => { if (!disabled) e.currentTarget.style.background = "rgba(59,130,246,0.22)"; }}
      onMouseOut={e  => { e.currentTarget.style.background = "rgba(59,130,246,0.13)"; }}>
      {loading
        ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("reportes.generando")}</>
        : <><i className="fa-solid fa-file-pdf"/> {t("reportes.pdf")}</>}
    </button>
  );
}

export function FiltrosBarra({ fechaDesde, setFechaDesde, fechaHasta, setFechaHasta,
    soloConMov, setSoloConMov, onActualizar, loading, extraButtons,
    comparativo, setComparativo }) {
  const { t } = useTranslation();
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 12,
      padding: "16px 18px", marginBottom: 16,
      display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
      {fechaDesde !== undefined && (
        <DateField
          label={t("reportes.desde")}
          value={fechaDesde}
          icon="fa-calendar" iconColor="#f97316"
          onChange={e => {
            const desde = e.target.value;
            setFechaDesde(desde);
            if (desde && setFechaHasta) {
              const [y, m] = desde.split("-").map(Number);
              const finMes = new Date(y, m, 0);
              const dd = String(finMes.getDate()).padStart(2, "0");
              const mm = String(m).padStart(2, "0");
              setFechaHasta(`${y}-${mm}-${dd}`);
            }
          }}
        />
      )}
      {fechaHasta !== undefined && (
        <DateField
          label={fechaDesde !== undefined ? t("reportes.hasta") : t("reportes.fechaCorte")}
          value={fechaHasta}
          icon="fa-calendar-check" iconColor="#4ade80"
          onChange={e => setFechaHasta(e.target.value)}
        />
      )}

      {/* Checkboxes estilo pill */}
      {(soloConMov !== undefined || setComparativo !== undefined) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {soloConMov !== undefined && (
            <CheckPill checked={soloConMov} onChange={setSoloConMov}
              color="#f97316" label={t("reportes.ocultarSinMovimiento")}/>
          )}
          {setComparativo !== undefined && (
            <CheckPill checked={!!comparativo} onChange={setComparativo}
              color="#4ade80" icon="fa-code-compare" label={t("reportes.comparativo")}/>
          )}
        </div>
      )}

      {/* Moneda + botones (PDF, etc.) alineados a la derecha */}
      <div style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <MonedaToggleInline />
        {extraButtons}
      </div>
    </div>
  );
}

export function CardsResumen({ cards }) {
  const { fmtCRC } = useCurrency();

  function fmtCard(card) {
    const v     = Number(card.value ?? 0);
    const isNeg = String(card.prefix || "").startsWith("-");
    return `${isNeg ? "-" : ""}${fmtCRC(v)}`;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
      {cards.map(card => (
        <div key={card.label} style={{ background: "var(--bg-card)", border: `1px solid ${card.color}30`,
          borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 9, flexShrink: 0,
            background: `${card.color}15`, border: `1px solid ${card.color}30`,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className={`fa-solid ${card.icon}`} style={{ color: card.color, fontSize: "0.95rem" }}/>
          </div>
          <div>
            <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "0.68rem", fontWeight: 600,
              textTransform: "uppercase", letterSpacing: "0.4px" }}>{card.label}</p>
            <p style={{ margin: "2px 0 0", color: "var(--text-primary)", fontSize: "0.92rem",
              fontWeight: 700, fontFamily: "monospace", whiteSpace: "nowrap" }}>
              {fmtCard(card)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function TableHeader({ count, loading, onColapsar, onExpandir }) {
  const { t } = useTranslation();
  return (
    <div className={`${invoiceStyles["table-header"]} d-flex justify-content-between align-items-center`}>
      <span style={{ fontSize: "0.81rem", color: "var(--text-secondary)" }}>
        {loading ? t("reportes.calculando") : `${count} ${t("reportes.cuentas")}`}
      </span>
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={onColapsar} style={{ padding: "3px 10px", background: "transparent",
          border: "1px solid var(--border-card)", borderRadius: 5,
          color: "var(--text-secondary)", fontSize: "0.74rem", cursor: "pointer" }}>
          {t("reportes.colapsarTodo")}
        </button>
        <button onClick={onExpandir} style={{ padding: "3px 10px", background: "transparent",
          border: "1px solid var(--border-card)", borderRadius: 5,
          color: "var(--text-secondary)", fontSize: "0.74rem", cursor: "pointer" }}>
          {t("reportes.expandirTodo")}
        </button>
      </div>
    </div>
  );
}

export function FilaCargando({ colSpan, msg }) {
  const { t } = useTranslation();
  return (
    <tr><td colSpan={colSpan} style={{ textAlign: "center", padding: 48, color: "var(--text-secondary)" }}>
      <i className="fa-solid fa-circle-notch fa-spin"
         style={{ color: "#f97316", fontSize: "1.4rem", display: "block", marginBottom: 10 }}/>
      {msg || t("reportes.calculando")}
    </td></tr>
  );
}

export function FilaVacia({ colSpan, soloConMov, onMostrarTodas }) {
  const { t } = useTranslation();
  return (
    <tr><td colSpan={colSpan} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
      {t("reportes.sinDatos")}
      {soloConMov && onMostrarTodas && (
        <button onClick={onMostrarTodas} style={{ marginLeft: 8, background: "none", border: "none",
          color: "#f97316", cursor: "pointer", fontSize: "0.83rem", textDecoration: "underline" }}>
          {t("reportes.mostrarTodas")}
        </button>
      )}
    </td></tr>
  );
}

export function FilaArbol({ fila, collapsed, onToggle, colorFn, valorFn, bgFn, colSpan = 1, extraCells }) {
  const indent = NIVEL_INDENT[fila.nivel] ?? 0;
  const rowBg  = bgFn ? bgFn(fila) : NIVEL_BG[fila.nivel];
  return (
    <tr style={{ background: rowBg, borderBottom: "1px solid var(--border-card)" }}>
      <td style={{ fontFamily: "monospace", fontSize: "0.75rem", color: "var(--text-muted)",
        whiteSpace: "nowrap", padding: "6px 10px" }}>
        {fila.codigo}
      </td>
      <td style={{ padding: "6px 8px", paddingLeft: indent + 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {fila.tiene_hijos ? (
            <button onClick={() => onToggle(fila.id)}
              style={{ background: "none", border: "none", cursor: "pointer",
                color: "var(--text-muted)", padding: 0, fontSize: "0.65rem", width: 14, flexShrink: 0 }}>
              <i className={`fa-solid fa-chevron-${collapsed.has(fila.id) ? "right" : "down"}`}/>
            </button>
          ) : <span style={{ width: 14, flexShrink: 0 }}/>}
          <span style={{ fontWeight: NIVEL_WEIGHT[fila.nivel],
            color: colorFn ? colorFn(fila) : NIVEL_COLOR[fila.nivel],
            fontSize: fila.nivel === 1 ? "0.88rem" : "0.83rem" }}>
            {fila.nombre}
          </span>
        </div>
      </td>
      <td className="text-end" style={{ fontFamily: "monospace", fontSize: "0.83rem",
        padding: "6px 14px", fontWeight: fila.nivel <= 2 ? 700 : 400,
        color: valorFn ? valorFn(fila).color : NIVEL_COLOR[fila.nivel] }}>
        {valorFn ? valorFn(fila).text : ""}
      </td>
      {extraCells ?? null}
    </tr>
  );
}
